import React, { useState, useEffect } from 'react';
import { ChevronDown, Search, Clock, List, Loader2, CheckCircle2 } from 'lucide-react';
import { useSimulationSocket } from '../hooks/useSimulationSocket';
import { SimulationCardSkeleton } from './skeletons';
import { getZIndexClass } from '../config/zIndex';

interface Simulation {
  id: number;
  name: string;
  description: string;
  audience_id: number;
  audience_name: string;
  created_at: string;
  segment_count: number;
  persona_count: number;
  simulation_response?: string; // Add to track completion status
  task?: string;
}

interface SimulationHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSimulation: (simulationId: number) => void;
}

const API_URL = import.meta.env.VITE_API_URL || '';

const SimulationHistoryPanel: React.FC<SimulationHistoryPanelProps> = ({
  isOpen,
  onClose,
  onSelectSimulation,
}) => {
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [selectedAudienceId, setSelectedAudienceId] = useState<number | null>(null);
  const [isAudienceDropdownOpen, setIsAudienceDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [runningSimulations, setRunningSimulations] = useState<Set<number>>(new Set());

  // WebSocket integration for live status updates with error handling
  useSimulationSocket({
    onSimulationStarted: (data) => {
      try {
        setRunningSimulations((prev) => new Set([...prev, data.simulation_id]));
      } catch (error) {
        console.error('Error handling simulation started:', error);
      }
    },
    onSimulationCompleted: (data) => {
      try {
        setRunningSimulations((prev) => {
          const newSet = new Set(prev);
          newSet.delete(data.simulation_id);
          return newSet;
        });
        // Refresh the specific simulation to get updated data
        refreshSingleSimulation(data.simulation_id);
      } catch (error) {
        console.error('Error handling simulation completed:', error);
      }
    },
    onSimulationFailed: (data) => {
      try {
        setRunningSimulations((prev) => {
          const newSet = new Set(prev);
          newSet.delete(data.simulation_id);
          return newSet;
        });
      } catch (error) {
        console.error('Error handling simulation failed:', error);
      }
    },
    autoConnect: true, // Always connect (not just when panel is open)
  });

  // Extract unique audiences from simulations data instead of fetching separately
  // This is much more efficient than calling /audience API since simulations already contain audience info
  const availableAudiences = React.useMemo(() => {
    const audienceMap = new Map<number, { id: number; name: string }>();
    simulations.forEach((sim) => {
      if (sim.audience_id && sim.audience_name) {
        audienceMap.set(sim.audience_id, {
          id: sim.audience_id,
          name: sim.audience_name,
        });
      }
    });
    return Array.from(audienceMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [simulations]);

  useEffect(() => {
    // Load all simulations when panel opens
    if (isOpen) {
      fetchSimulations();
    }
  }, [isOpen]);

  useEffect(() => {
    // Fetch simulations when selected audience changes
    if (selectedAudienceId) {
      fetchSimulations(selectedAudienceId);
    } else {
      fetchSimulations();
    }
  }, [selectedAudienceId]);

  const fetchSimulations = async (audienceId?: number) => {
    try {
      setIsLoading(true);
      let url = `${API_URL}/simulations`;
      if (audienceId) {
        url += `?audience_id=${audienceId}`;
      }

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch simulations');
      }

      const data = await response.json();
      setSimulations(data);

      // Identify simulations without response (still running)
      const running = data
        .filter((sim: Simulation) => !sim.simulation_response)
        .map((sim: Simulation) => sim.id);

      setRunningSimulations(new Set(running));
    } catch (err) {
      console.error('Error fetching simulations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh a single simulation when it completes
  const refreshSingleSimulation = async (simulationId: number) => {
    try {
      const response = await fetch(`${API_URL}/simulations/${simulationId}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('Failed to refresh simulation:', simulationId);
        return;
      }

      const updatedSimulation = await response.json();

      // Update the simulation in the list
      setSimulations((prev) =>
        prev.map((sim) =>
          sim.id === simulationId
            ? {
                id: sim.id,
                name: sim.name,
                description: sim.description,
                audience_id: sim.audience_id,
                audience_name: sim.audience_name,
                created_at: sim.created_at,
                segment_count: sim.segment_count,
                persona_count: sim.persona_count,
                simulation_response: updatedSimulation.simulation_response,
              }
            : sim,
        ),
      );
    } catch (err) {
      console.error('Error refreshing single simulation:', err);
    }
  };

  const filteredSimulations = simulations.filter(
    (simulation) =>
      simulation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      simulation.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div
      className={`fixed inset-y-0 left-0 bg-white shadow-xl w-70 transform transition-transform duration-300 ease-in-out ${getZIndexClass('sidePanel')} ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <div className="flex flex-col h-full">
        <div className="p-2 border-b border-gray-200">
          <h2 className="text-lg font-medium flex items-center">
            <Clock className="w-5 h-5 mr-2" />
            Simulation History
          </h2>
        </div>

        <div className="p-4 border-b border-gray-200">
          <div className="relative mb-4">
            <button
              onClick={() => setIsAudienceDropdownOpen(!isAudienceDropdownOpen)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            >
              <span>
                {selectedAudienceId
                  ? availableAudiences.find((a) => a.id === selectedAudienceId)?.name
                  : 'All Audiences'}
              </span>
              <ChevronDown className="h-4 w-4" />
            </button>

            {isAudienceDropdownOpen && (
              <div
                className={`absolute ${getZIndexClass('dropdown')} mt-1 w-full bg-white rounded-md shadow-lg max-h-56 overflow-auto py-1 border border-gray-200`}
              >
                <button
                  onClick={() => {
                    setSelectedAudienceId(null);
                    setIsAudienceDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                >
                  All Audiences
                </button>
                {availableAudiences.map((audience) => (
                  <button
                    key={audience.id}
                    onClick={() => {
                      setSelectedAudienceId(audience.id);
                      setIsAudienceDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    {audience.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <input
              type="text"
              placeholder="Search simulations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <SimulationCardSkeleton key={i} />
              ))}
            </div>
          ) : filteredSimulations.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <List className="h-12 w-12 mx-auto opacity-30 mb-3" />
              <p>No simulations found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredSimulations.map((simulation) => (
                <button
                  key={simulation.id}
                  onClick={() => onSelectSimulation(simulation.id)}
                  className="w-full text-left p-3 rounded-lg hover:bg-blue-50 transition-colors border border-gray-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-blue-600">{simulation.name}</div>
                    {/* Status indicator */}
                    {runningSimulations.has(simulation.id) ? (
                      <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded-full">
                        <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                        <span className="text-xs text-blue-600 font-medium">Running</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 rounded-full">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        <span className="text-xs text-green-600 font-medium">Complete</span>
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {(
                      simulation.description?.replace(/vitamin concept test/gi, 'Concept Test') ||
                      'No description'
                    ).substring(0, 60)}
                    {(simulation.description?.length || 0) > 60 ? '...' : ''}
                  </div>
                  <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                    {!['concept-test', 'driving-concept-test', 'vitamin-concept-test'].includes(
                      simulation.task || '',
                    ) &&
                      simulation.audience_name !== 'Driving Simulations' && (
                        <span>{simulation.audience_name}</span>
                      )}
                    <span>{formatDate(simulation.created_at)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimulationHistoryPanel;
